import React, { useEffect, useState, useRef } from "react";
import { X, Upload, Trash2, GripVertical, Plus, MapPin } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition";
const LABEL = "block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5";

export default function DestinationEditModal({ slug, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);
    const coverInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);

    // sub-region inline create form
    const [newSub, setNewSub] = useState({ name: "", slug: "" });
    const [showNewSub, setShowNewSub] = useState(false);

    useEffect(() => {
        if (!slug) return;
        api.get(`/admin/destinations/${slug}`).then((r) => setData(r.data));
    }, [slug]);

    if (!data) return null;

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                name: data.name,
                country: data.country || "Türkiye",
                region: data.region,
                description: data.description,
                image: data.image,
                gallery: data.gallery || [],
                is_active: data.is_active,
                sort_order: data.sort_order || 0,
            };
            await api.patch(`/admin/destinations/${slug}`, payload);
            toast.success("Kaydedildi");
            onChanged?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydetme başarısız");
        } finally {
            setSaving(false);
        }
    };

    const uploadFiles = async (files, target /* 'cover' | 'gallery' */) => {
        const arr = Array.from(files || []);
        if (arr.length === 0) return;
        const uploaded = [];
        for (const f of arr) {
            if (!f.type.startsWith("image/")) continue;
            const fd = new FormData();
            fd.append("file", f);
            try {
                const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(r.data.url);
            } catch (e) {
                toast.error(`'${f.name}' yüklenemedi`);
            }
        }
        if (uploaded.length === 0) return;
        if (target === "cover") {
            set("image", uploaded[0]);
            toast.success("Kapak görseli güncellendi");
        } else {
            // append to gallery via API (persists immediately)
            try {
                const r = await api.post(`/admin/destinations/${slug}/gallery/add`, { urls: uploaded });
                set("gallery", r.data.gallery);
                toast.success(`${uploaded.length} fotoğraf eklendi`);
            } catch (e) {
                toast.error("Galeri güncellenemedi");
            }
        }
    };

    const removeFromGallery = async (idx) => {
        try {
            const r = await api.delete(`/admin/destinations/${slug}/gallery/${idx}`);
            set("gallery", r.data.gallery);
        } catch (e) {
            toast.error("Silme başarısız");
        }
    };

    const reorderGallery = async (from, to) => {
        if (from === to || from == null || to == null) return;
        const arr = [...(data.gallery || [])];
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        set("gallery", arr);
        try {
            await api.post(`/admin/destinations/${slug}/gallery/reorder`, { gallery: arr });
        } catch (e) {
            toast.error("Sıralama kaydedilemedi");
        }
    };

    // sub-region create
    const createSubRegion = async () => {
        if (!newSub.name) { toast.error("Alt bölge adı gerekli"); return; }
        const slugify = (s) => s.toLowerCase()
            .replace(/ı/g, "i").replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ö/g, "o")
            .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        const childSlug = newSub.slug || slugify(newSub.name);
        try {
            await api.post("/admin/destinations", {
                slug: childSlug,
                name: newSub.name,
                country: data.country || "Türkiye",
                region: data.region,
                parent_slug: slug,
                is_active: true,
                sort_order: 0,
            });
            toast.success("Alt bölge eklendi");
            setShowNewSub(false);
            setNewSub({ name: "", slug: "" });
            // refresh data to get new children
            const r = await api.get(`/admin/destinations/${slug}`);
            setData(r.data);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eklenemedi");
        }
    };

    const toggleSubActive = async (childSlug) => {
        try {
            await api.post(`/admin/destinations/${childSlug}/toggle-active`);
            const r = await api.get(`/admin/destinations/${slug}`);
            setData(r.data);
            onChanged?.();
        } catch (e) {
            toast.error("İşlem başarısız");
        }
    };

    const deleteSub = async (childSlug, childName) => {
        if (!window.confirm(`'${childName}' alt bölgesini silmek istiyor musunuz?`)) return;
        try {
            await api.delete(`/admin/destinations/${childSlug}`);
            const r = await api.get(`/admin/destinations/${slug}`);
            setData(r.data);
            onChanged?.();
            toast.success("Silindi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silme başarısız");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="destination-edit-modal">
            <div className="w-full max-w-4xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-sky-500/10 flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">{data.name}</h2>
                            <p className="text-xs text-slate-500">{data.region} · {(data.children || []).length} alt bölge · {data.hotel_count} otel</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="destination-modal-close"><X className="h-5 w-5" /></button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {/* Basic info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={LABEL}>İsim</label>
                            <input value={data.name || ""} onChange={(e) => set("name", e.target.value)} className={INPUT} data-testid="dest-name" />
                        </div>
                        <div>
                            <label className={LABEL}>Bölge</label>
                            <input value={data.region || ""} onChange={(e) => set("region", e.target.value)} className={INPUT} placeholder="örn: Akdeniz" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={LABEL}>Açıklama</label>
                            <textarea rows={3} value={data.description || ""} onChange={(e) => set("description", e.target.value)} className={INPUT} placeholder="Bu destinasyon hakkında kısa açıklama..." />
                        </div>
                    </div>

                    {/* Cover image */}
                    <div>
                        <label className={LABEL}>Kapak Görseli</label>
                        <div className="flex items-center gap-4">
                            {data.image ? (
                                <div className="relative">
                                    <img src={resolveImageUrl(data.image)} alt="Cover" className="h-28 w-44 object-cover rounded-xl border border-slate-200 dark:border-slate-700" />
                                    <button onClick={() => set("image", "")} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"><X className="h-3.5 w-3.5" /></button>
                                </div>
                            ) : (
                                <div className="h-28 w-44 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center text-slate-400 text-xs">Görsel yok</div>
                            )}
                            <input ref={coverInputRef} type="file" accept="image/*" onChange={(e) => uploadFiles(e.target.files, "cover")} className="hidden" />
                            <button onClick={() => coverInputRef.current?.click()} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-semibold flex items-center gap-2" data-testid="dest-cover-upload">
                                <Upload className="h-4 w-4" /> Kapak Yükle
                            </button>
                        </div>
                    </div>

                    {/* Gallery with drag-drop */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className={LABEL + " mb-0"}>Galeri ({(data.gallery || []).length})</label>
                            <button onClick={() => fileInputRef.current?.click()} className="text-xs font-semibold text-sky-600 hover:underline flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> Çoklu yükle</button>
                            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={(e) => uploadFiles(e.target.files, "gallery")} className="hidden" />
                        </div>
                        <div
                            data-testid="dest-gallery-dropzone"
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragOver(false);
                                if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files, "gallery");
                            }}
                            className={`p-4 rounded-xl border-2 border-dashed transition-colors ${dragOver ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-300 dark:border-slate-700"}`}
                        >
                            {(data.gallery || []).length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-500">
                                    <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                                    Fotoğrafları buraya sürükleyin veya çoklu yükle butonunu kullanın
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {(data.gallery || []).map((img, idx) => (
                                        <div
                                            key={idx}
                                            draggable
                                            onDragStart={() => setDragIndex(idx)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); reorderGallery(dragIndex, idx); setDragIndex(null); }}
                                            className={`relative group rounded-xl overflow-hidden border-2 cursor-move ${dragIndex === idx ? "border-sky-500 ring-2 ring-sky-300" : "border-slate-200 dark:border-slate-700"}`}
                                            data-testid={`dest-gallery-item-${idx}`}
                                        >
                                            <img src={resolveImageUrl(img)} alt={`Galeri ${idx + 1}`} className="w-full h-28 object-cover" />
                                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-mono flex items-center gap-1">
                                                <GripVertical className="h-3 w-3" /> {idx + 1}
                                            </div>
                                            <button onClick={() => removeFromGallery(idx)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><X className="h-3.5 w-3.5" /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">💡 Fotoğraf sırasını değiştirmek için sürükleyip bırakın.</p>
                    </div>

                    {/* Sub-regions (only if not itself a sub-region) */}
                    {!data.parent_slug && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className={LABEL + " mb-0"}>Alt Bölgeler ({(data.children || []).length})</label>
                                {!showNewSub && (
                                    <button onClick={() => setShowNewSub(true)} className="text-xs font-semibold text-sky-600 hover:underline flex items-center gap-1" data-testid="dest-add-sub-btn"><Plus className="h-3.5 w-3.5" /> Alt bölge ekle</button>
                                )}
                            </div>
                            {showNewSub && (
                                <div className="p-3 mb-3 rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30">
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                        <div>
                                            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Adı</label>
                                            <input value={newSub.name} onChange={(e) => setNewSub((s) => ({ ...s, name: e.target.value }))} placeholder="örn: Belek" className={INPUT} data-testid="dest-new-sub-name" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Slug (boş bırakılırsa otomatik)</label>
                                            <input value={newSub.slug} onChange={(e) => setNewSub((s) => ({ ...s, slug: e.target.value }))} placeholder="belek" className={INPUT} />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={createSubRegion} className="px-4 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold" data-testid="dest-new-sub-save">Ekle</button>
                                        <button onClick={() => { setShowNewSub(false); setNewSub({ name: "", slug: "" }); }} className="px-4 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-semibold">İptal</button>
                                    </div>
                                </div>
                            )}
                            {(data.children || []).length === 0 ? (
                                <div className="py-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">Henüz alt bölge yok</div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {data.children.map((c) => (
                                        <div key={c.slug} className="flex items-center justify-between gap-2 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className={`h-2 w-2 rounded-full ${c.is_active === false ? "bg-slate-400" : "bg-emerald-500"}`} />
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold truncate text-slate-900 dark:text-white">{c.name}</div>
                                                    <div className="text-[10px] text-slate-500">{c.hotel_count} otel</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => toggleSubActive(c.slug)} className="px-2 py-1 rounded-lg text-xs font-semibold bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700" data-testid={`dest-sub-toggle-${c.slug}`}>{c.is_active === false ? "Aktifleştir" : "Pasifleştir"}</button>
                                                <button onClick={() => deleteSub(c.slug, c.name)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white dark:bg-slate-900 px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between rounded-b-2xl">
                    <div className="text-xs text-slate-500">Slug: <code className="font-mono">{slug}</code></div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-semibold">İptal</button>
                        <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="dest-save">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
